import { TEST_ID_WATCHLIST } from "e2e/testId";
import { ChevronsUpDownIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import ManageActionSheet from "@/components/organisms/ManageActionSheet";
import MenuContent from "@/components/organisms/MenuContent";

import MobileActionMenu, {
  type MobileActionMenuItem,
} from "@/components/molecules/MobileActionMenu";
// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: unavoidable primitive usage outside atoms (@/components/ui import(s): @/components/ui/button) —…
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import {
  Popover,
  PopoverBackdrop,
  PopoverContent,
} from "@/components/atoms/Popover";
import { WatchlistWatchlistMetaFragment } from "@/generated/graphql";

// design-system: dynamic-style waiver — measured-width clamp (Math.min on window width)
const buttonTextMaxWidthStyle = (widthName: number) => ({
  maxWidth: Math.min(widthName * 0.4, 150),
});

/**
 * Props for PopoverMenuView component
 */
interface Props {
  selectedWatchlist?: WatchlistWatchlistMetaFragment | null;
  watchlists: WatchlistWatchlistMetaFragment[];
  onSelect: (value: string) => void;
  isOpen: boolean;
  widthName: number;
  maxHeight: number;
  onClose: () => void;
  onOpen: () => void;
  onAddWatchlistPress: () => void;
  loading: boolean;
  showActionsheet: boolean;
  onActionsheetClose: () => void;
  onActionsheetOpen: () => void;
  isDark: boolean;
  onMyRosterOpen?: () => void;
  readonly bottomInset: number;
  renderTrigger: (triggerProps: Record<string, unknown>) => React.JSX.Element;
  /** Pre-wired rows for the native MobileActionMenu (SE-5546). */
  readonly nativeMenuItems: readonly MobileActionMenuItem[];
}

/**
 * View component for popover menu displaying watchlist selection, creation, and management
 * @param root0 - Component props
 * @param root0.showActionsheet - Whether manage lists action sheet is visible
 * @param root0.onActionsheetClose - Callback invoked when action sheet closes
 * @param root0.selectedWatchlist - Currently selected watchlist to highlight with checkmark
 * @param root0.watchlists - Array of available watchlists to display in dropdown
 * @param root0.onSelect - Callback invoked when watchlist is selected with watchlist ID
 * @param root0.isOpen - Whether popover menu is currently open
 * @param root0.widthName - Window width for responsive text truncation
 * @param root0.maxHeight - Maximum height for dropdown on mobile to prevent viewport overflow
 * @param root0.onClose - Callback invoked when popover menu closes
 * @param root0.onOpen - Callback invoked when popover menu opens
 * @param root0.onAddWatchlistPress - Callback invoked when "New List" button is pressed
 * @param root0.onActionsheetOpen - Callback invoked when "Manage Lists" button is pressed
 * @param root0.onMyRosterOpen - Callback invoked when "My Roster" option is selected
 * @param root0.loading - Whether watchlist creation is in progress
 * @param root0.isDark - Whether using dark theme styling
 * @param root0.bottomInset - Bottom safe area inset value for proper padding
 * @param root0.renderTrigger - Function to render trigger button for web popover
 * @param root0.nativeMenuItems - Pre-wired rows for the native MobileActionMenu (SE-5546)
 */
const PopoverMenuView = ({
  showActionsheet,
  onActionsheetClose,
  selectedWatchlist,
  watchlists,
  onSelect,
  isOpen,
  widthName,
  maxHeight,
  onClose,
  onOpen,
  onAddWatchlistPress,
  onActionsheetOpen,
  onMyRosterOpen,
  loading,
  isDark,
  bottomInset,
  renderTrigger,
  nativeMenuItems,
}: Props) =>
  Platform.OS !== "web" ? (
    <>
      <Button
        testID={TEST_ID_WATCHLIST.POPOVER_OPEN_BUTTON}
        className="min-w-0 shrink gap-2 overflow-hidden"
        variant="link"
        onPress={onOpen}
      >
        <ButtonText
          // eslint-disable-next-line no-restricted-syntax -- measured-width clamp (Math.min on window width); off the closed sizing ladder and ButtonText is a waived @/components/ui primitive (non-collapsible Button composition) with no atom to swap to
          style={buttonTextMaxWidthStyle(widthName)}
          className="font-heading text-title text-status-info"
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {selectedWatchlist?.name ?? "My Roster"}
        </ButtonText>
        <ButtonIcon
          as={ChevronsUpDownIcon}
          className="text-status-info"
          size="md"
        />
      </Button>
      <MobileActionMenu
        isOpen={isOpen}
        onClose={onClose}
        items={nativeMenuItems}
        testID="watchlist-popover-menu"
      />
      {showActionsheet ? (
        <ManageActionSheet
          show={showActionsheet}
          onClose={onActionsheetClose}
        />
      ) : null}
    </>
  ) : (
    <>
      <Popover
        isOpen={isOpen}
        onClose={onClose}
        onOpen={onOpen}
        placement="bottom"
        offset={8}
        crossOffset={16}
        size="lg"
        trigger={renderTrigger}
      >
        <PopoverBackdrop />
        <PopoverContent
          className={`min-w-64 flex-1 rounded-md border-0 translate-x-6 ${isDark ? "bg-surface-action" : "bg-surface-raised"} px-2 py-1`}
        >
          <MenuContent
            watchlists={watchlists}
            selectedWatchlist={selectedWatchlist}
            onSelect={onSelect}
            onClose={onClose}
            onMyRosterOpen={onMyRosterOpen}
            onAddWatchlistPress={onAddWatchlistPress}
            onActionsheetOpen={onActionsheetOpen}
            loading={loading}
            isDark={isDark}
            widthName={widthName}
            bottomInset={bottomInset}
            maxHeight={maxHeight}
            isNative={false}
          />
        </PopoverContent>
      </Popover>
      {showActionsheet ? (
        <ManageActionSheet
          show={showActionsheet}
          onClose={onActionsheetClose}
        />
      ) : null}
    </>
  );

PopoverMenuView.displayName = "PopoverMenuView";

export default memo(PopoverMenuView);
